import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * Hook to load attendance dashboard data in a single optimized RPC call
 * Replaces 3 sequential queries (balance, entitlements, requests) with 1 call
 *
 * @param {string} userId - User profile ID
 * @param {number} year - Year to fetch data for
 * @returns {Object} - { data, loading, error, refetch }
 */
export function useAttendanceDashboard(userId, year) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);
  const abortControllerRef = useRef(null);

  /**
   * Load attendance dashboard data using the optimized RPC
   */
  const loadDashboardData = useCallback(async () => {
    if (!userId || !year) {
      console.log('[useAttendanceDashboard] Missing userId or year');
      setLoading(false);
      return null;
    }

    // Prevent concurrent requests
    if (loadingRef.current) {
      console.log('[useAttendanceDashboard] Already loading, skipping...');
      return null;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('[useAttendanceDashboard] Loading data with single RPC call...');
      console.time('[useAttendanceDashboard] RPC call duration');

      // Single RPC call replaces 3 sequential queries!
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'get_user_attendance_dashboard',
        {
          p_user_id: userId,
          p_year: year
        }
      );

      console.timeEnd('[useAttendanceDashboard] RPC call duration');

      if (rpcError) {
        console.error('[useAttendanceDashboard] RPC error:', rpcError);
        setError(rpcError.message);
        setData(null);
        return null;
      }

      console.log('[useAttendanceDashboard] RPC response:', {
        hasBalance: !!rpcData?.balance,
        hasStats: !!rpcData?.stats,
        recentRequestsCount: rpcData?.recent_requests?.length || 0,
        upcomingLeaveCount: rpcData?.upcoming_leave?.length || 0
      });

      setData(rpcData);
      return rpcData;
    } catch (err) {
      // Ignore abort errors
      if (err.name === 'AbortError') {
        return null;
      }
      console.error('[useAttendanceDashboard] Error:', err);
      setError(err.message);
      setData(null);
      return null;
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [userId, year]);

  // Auto-load when userId or year changes
  useEffect(() => {
    loadDashboardData();

    // Cleanup on unmount or when deps change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadDashboardData]);

  return {
    data,
    loading,
    error,
    refetch: loadDashboardData,
    // Convenience accessors
    balance: data?.balance || null,
    stats: data?.stats || {
      entitled: 0,
      taken: 0,
      remaining: 0,
      pending_requests: 0,
      on_leave_today: 0
    },
    recentRequests: data?.recent_requests || [],
    upcomingLeave: data?.upcoming_leave || []
  };
}

/**
 * Hook for admin to view another user's attendance dashboard
 * Same as useAttendanceDashboard but with explicit user targeting
 *
 * @param {string} targetUserId - Target user profile ID (requires attendance.manage permission)
 * @param {number} year - Year to fetch data for
 * @returns {Object} - { data, loading, error, refetch }
 */
export function useUserAttendanceDashboard(targetUserId, year) {
  return useAttendanceDashboard(targetUserId, year);
}
